import React, { useEffect, useMemo, useState } from 'react';
import {
    AlertBanner,
    CardSurface,
    DropdownItem,
    Modal,
    Search,
    SparkModalPortal,
    Spinner,
    Text,
} from '@spark';
import {
    COLUMN_OVERLAP,
    classifyColumnOverlap,
    columnsFromLibraryOption,
    SHARED_COLUMNS_LIBRARY_ERROR,
} from '@/features/datasets/utils/sharedFileColumns';

const TITLE_BY_KIND = {
    dataset: 'Choose Dataset from your uploads',
    benchmark: 'Choose Benchmark from your uploads',
};

const SUBTITLE_BY_KIND = {
    dataset: 'Find and select the datasets you want to add from existing uploads',
    benchmark: 'Find and select the benchmarks you want to add from existing uploads',
};

const EMPTY_BY_KIND = {
    dataset: 'No datasets found in your library.',
    benchmark: 'No benchmarks found in your library.',
};

const SELECTED_NOUN_BY_KIND = {
    dataset: { one: 'dataset', many: 'datasets' },
    benchmark: { one: 'benchmark', many: 'benchmarks' },
};

export function selectedLibraryCountLabel(kind, count) {
    const nouns = SELECTED_NOUN_BY_KIND[kind] || SELECTED_NOUN_BY_KIND.dataset;
    const noun = count === 1 ? nouns.one : nouns.many;
    return `${count} ${noun} selected`;
}

const BODY_STACK_STYLE = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--dimension-spacing-12)',
    width: '100%',
};

const LIST_STACK_STYLE = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--dimension-spacing-04)',
    width: '100%',
};

const LOADING_STYLE = {
    display: 'flex',
    justifyContent: 'center',
    padding: 'var(--dimension-spacing-24)',
};

/**
 * Multi-select library picker. Does not open the legacy DatasetUploadStepsModal —
 * the parent seeds the Data Collection panel.
 *
 * Done calls `onPick` with the selected options and does not call `onClose`.
 * `onClose` is dismiss only (Cancel / X / Escape).
 */
const LibraryPickModal = ({
    open,
    onClose,
    onPick,
    kind = 'dataset',
    title,
    options = [],
    loading = false,
}) => {
    const [query, setQuery] = useState('');
    const [selectedValues, setSelectedValues] = useState(() => new Set());

    useEffect(() => {
        if (!open) {
            setQuery('');
            setSelectedValues(new Set());
        }
    }, [open]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return (options || []).filter(
            (option) => !q || String(option.value || '').toLowerCase().includes(q)
        );
    }, [options, query]);

    const toggleValue = (value) => {
        setSelectedValues((prev) => {
            const next = new Set(prev);
            if (next.has(value)) next.delete(value);
            else next.add(value);
            return next;
        });
    };

    const selectedOptions = useMemo(
        () => (options || []).filter((option) => selectedValues.has(option.value)),
        [options, selectedValues]
    );

    const selectedOverlap = useMemo(
        () => classifyColumnOverlap(selectedOptions.map(columnsFromLibraryOption)),
        [selectedOptions]
    );

    const hasNoSharedColumns = selectedOverlap.status === COLUMN_OVERLAP.none;

    const handleDone = () => {
        if (!selectedOptions.length || hasNoSharedColumns) return;
        onPick?.(selectedOptions);
    };

    const resolvedKind = TITLE_BY_KIND[kind] ? kind : 'dataset';

    return (
        <SparkModalPortal
            open={open}
            onClose={onClose}
            destroyOnClose
            size="text"
            className="task-upload-data-modal"
        >
            <Modal
                title={title ?? TITLE_BY_KIND[resolvedKind]}
                subtitle={SUBTITLE_BY_KIND[resolvedKind]}
                showSubtitle
                showScrollBar
                fixedHeight={false}
                showFooter
                showOverlay={false}
                showClose
                primaryLabel="Done"
                tertiaryLabel="Cancel"
                primaryDisabled={loading || selectedValues.size === 0 || hasNoSharedColumns}
                showFooterSlot
                footerSlot={
                    <Text
                        as="p"
                        variant="base-regular"
                        color="secondary"
                        aria-live="polite"
                    >
                        {selectedLibraryCountLabel(resolvedKind, selectedValues.size)}
                    </Text>
                }
                onPrimaryClick={handleDone}
                onTertiaryClick={onClose}
                onCloseClick={onClose}
            >
                {loading ? (
                    <div style={LOADING_STYLE}>
                        <Spinner size="medium" />
                    </div>
                ) : (
                    <div style={BODY_STACK_STYLE}>
                        <Search
                            size="medium"
                            placeholder="Search your uploads…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onClear={() => setQuery('')}
                        />
                        <div
                            role="listbox"
                            aria-multiselectable="true"
                            aria-label={TITLE_BY_KIND[resolvedKind]}
                            style={LIST_STACK_STYLE}
                        >
                            {filtered.map((option) => {
                                const value = option.value;
                                const isSelected = selectedValues.has(value);
                                return (
                                    <CardSurface
                                        key={value}
                                        bordered
                                        padding="none"
                                        background="white"
                                    >
                                        <DropdownItem
                                            label={option.label || value}
                                            state={isSelected ? 'selected' : 'default'}
                                            onClick={() => toggleValue(value)}
                                        />
                                    </CardSurface>
                                );
                            })}
                        </div>
                        {!filtered.length && (
                            <Text variant="base-regular" color="secondary" as="p">
                                {(options || []).length
                                    ? 'No matches.'
                                    : EMPTY_BY_KIND[resolvedKind]}
                            </Text>
                        )}
                        {hasNoSharedColumns ? (
                            <AlertBanner
                                feedback="error"
                                text={SHARED_COLUMNS_LIBRARY_ERROR}
                                showButton={false}
                                showCloseButton={false}
                            />
                        ) : null}
                    </div>
                )}
            </Modal>
        </SparkModalPortal>
    );
};

export default LibraryPickModal;
